import { useState } from 'preact/hooks'
import type { GalleryItem } from '~/types'
import Img from '~/components/Img'
import Lightbox from './Lightbox'

interface GalleryProps {
	items: GalleryItem[]
	cols?: number
}

// Responsive masonry (CSS columns) so each image keeps its natural orientation — many
// shots are vertical and would be badly cropped by a fixed aspect ratio. Thumbnails
// lazy-load; clicking one opens the Lightbox. Literal class strings (not interpolated)
// so Tailwind keeps them.
const columnClass: Record<number, string> = {
	1: 'columns-1',
	2: 'columns-1 sm:columns-2',
	3: 'columns-1 sm:columns-2 lg:columns-3',
}

export default function Gallery({ items, cols }: GalleryProps) {
	const [index, setIndex] = useState<number | null>(null)

	// Never use more columns than there are images, so small galleries display large.
	const effective = Math.min(cols ?? 3, items.length)

	return (
		<>
			<div className={`${columnClass[effective] ?? columnClass[1]} gap-5 [column-fill:balance]`}>
				{items.map((item, i) => (
					<button
						key={item.src}
						type="button"
						onClick={() => setIndex(i)}
						className="mb-5 block w-full break-inside-avoid cursor-pointer border-none p-0 bg-black"
						aria-label={`Open ${item.alt}`}
					>
						<Img
							asset={item.src}
							tier="thumb"
							sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 90vw"
							alt={item.alt}
							className="transition duration-300 hover:opacity-85"
						/>
					</button>
				))}
			</div>
			{index !== null && (
				<Lightbox
					items={items}
					index={index}
					onClose={() => setIndex(null)}
					onIndexChange={setIndex}
				/>
			)}
		</>
	)
}

Gallery.defaultProps = {
	cols: 3,
}
